import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { LuChevronRight, LuPanelLeftClose, LuPanelLeftOpen } from 'react-icons/lu';
import logo from '../../assets/img/logo.png';
import { myRole } from '../../services/roleServices';
import { useSidebar } from '../../contexts/SidebarContext';
import { NAV_BY_ROLE, groupHasActive, isItemActive } from './navConfig';

const groupKey = (path, label) => `${path}/${label}`;

/* Hover/focus tooltip used only while the desktop rail is collapsed. */
const useRailTooltip = (enabled) => {
  const [tip, setTip] = useState(null);
  const show = useCallback(
    (event, label) => {
      if (!enabled) return;
      const rect = event.currentTarget.getBoundingClientRect();
      setTip({ label, top: rect.top + rect.height / 2, left: rect.right + 10 });
    },
    [enabled]
  );
  const hide = useCallback(() => setTip(null), []);
  useEffect(() => { if (!enabled) setTip(null); }, [enabled]);
  return { tip, show, hide };
};

const NavItems = ({ items, depth, path, pathname, openGroups, onToggleGroup, onNavigate, tooltip, collapsed, onExpandRail }) => (
  <ul className={depth === 0 ? 'nav-list' : 'nav-list nav-list--nested'}>
    {items.map((item) => {
      const Icon = item.icon;
      const tipHandlers = depth === 0 && collapsed
        ? {
            onMouseEnter: (e) => tooltip.show(e, item.label),
            onMouseLeave: tooltip.hide,
            onFocus: (e) => tooltip.show(e, item.label),
            onBlur: tooltip.hide,
          }
        : {};

      if (item.children) {
        const key = groupKey(path, item.label);
        const active = groupHasActive(item, pathname);
        const open = openGroups[key] ?? active;
        const listId = `nav-${key.replace(/[^a-z0-9]+/gi, '-').toLowerCase()}`;
        return (
          <li key={key}>
            <button
              type="button"
              className={`nav-group-toggle${active ? ' has-active' : ''}`}
              aria-expanded={open}
              aria-controls={listId}
              aria-label={collapsed && depth === 0 ? item.label : undefined}
              onClick={() => {
                if (collapsed && depth === 0) {
                  onExpandRail();
                  onToggleGroup(key, true);
                } else {
                  onToggleGroup(key, !open);
                }
              }}
              {...tipHandlers}
            >
              {Icon && <Icon className="app-nav-icon" aria-hidden="true" />}
              <span className="nav-label">{item.label}</span>
              <LuChevronRight className="nav-chevron" aria-hidden="true" />
            </button>
            {open && (
              <div id={listId}>
                <NavItems
                  items={item.children}
                  depth={depth + 1}
                  path={key}
                  pathname={pathname}
                  openGroups={openGroups}
                  onToggleGroup={onToggleGroup}
                  onNavigate={onNavigate}
                  tooltip={tooltip}
                  collapsed={collapsed}
                  onExpandRail={onExpandRail}
                />
              </div>
            )}
          </li>
        );
      }

      const active = isItemActive(item, pathname);
      return (
        <li key={item.to}>
          <NavLink
            to={item.to}
            end
            className={`nav-item-link${active ? ' is-active' : ''}`}
            aria-current={active ? 'page' : undefined}
            aria-label={collapsed && depth === 0 ? item.label : undefined}
            onClick={onNavigate}
            {...tipHandlers}
          >
            {Icon && <Icon className="app-nav-icon" aria-hidden="true" />}
            <span className="nav-label">{item.label}</span>
          </NavLink>
        </li>
      );
    })}
  </ul>
);

const Sidebar = () => {
  const { collapsed, mobileOpen, closeSidebar, toggleCollapsed } = useSidebar();
  const { pathname } = useLocation();
  const role = myRole();
  const sections = useMemo(() => NAV_BY_ROLE[role] || [], [role]);

  // Explicit open/closed choices; groups containing the active route default to open.
  const [openGroups, setOpenGroups] = useState({});
  const onToggleGroup = useCallback((key, open) => setOpenGroups((prev) => ({ ...prev, [key]: open })), []);
  const tooltip = useRailTooltip(collapsed && !mobileOpen);

  // Close the mobile drawer on navigation and on Escape.
  useEffect(() => { closeSidebar(); }, [pathname, closeSidebar]);
  useEffect(() => {
    if (!mobileOpen) return undefined;
    const onKey = (e) => { if (e.key === 'Escape') closeSidebar(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [mobileOpen, closeSidebar]);

  const railCollapsed = collapsed && !mobileOpen;

  return (
    <>
      <aside
        id="app-sidebar"
        className={`app-sidebar${collapsed ? ' is-collapsed' : ''}${mobileOpen ? ' is-mobile-open' : ''}`}
        aria-label="Main navigation"
      >
        <div className="app-sidebar__brand">
          <img src={logo} alt="Scientex Conferences" width="176" />
          <span className="app-sidebar__brand-mark" aria-hidden="true">S</span>
        </div>

        <nav className="app-sidebar__nav">
          {sections.map((section, index) => (
            <div key={section.section || `section-${index}`}>
              {section.section && <div className="app-sidebar__section">{section.section}</div>}
              <NavItems
                items={section.items}
                depth={0}
                path={section.section || `s${index}`}
                pathname={pathname}
                openGroups={openGroups}
                onToggleGroup={onToggleGroup}
                onNavigate={tooltip.hide}
                tooltip={tooltip}
                collapsed={railCollapsed}
                onExpandRail={toggleCollapsed}
              />
            </div>
          ))}
        </nav>

        <div className="app-sidebar__footer">
          <button
            type="button"
            className="app-sidebar__collapse"
            onClick={toggleCollapsed}
            aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
            aria-pressed={collapsed}
          >
            {collapsed
              ? <LuPanelLeftOpen className="app-nav-icon" aria-hidden="true" />
              : <LuPanelLeftClose className="app-nav-icon" aria-hidden="true" />}
            <span className="nav-label">Collapse</span>
          </button>
        </div>
      </aside>

      <div
        className={`app-sidebar-backdrop${mobileOpen ? ' is-visible' : ''}`}
        onClick={closeSidebar}
        aria-hidden="true"
      />

      {tooltip.tip && (
        <div className="nav-tooltip" role="tooltip" style={{ top: tooltip.tip.top, left: tooltip.tip.left }}>
          {tooltip.tip.label}
        </div>
      )}
    </>
  );
};

export default Sidebar;
